import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Box,
  Divider,
  Chip,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Alert,
  TableFooter
} from '@mui/material';
import {
  Print as PrintIcon,
  ArrowBack,
  PictureAsPdf,
  CheckCircle,
  Error as ErrorIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import api from '../../services/api';

const CommissionRequestPrint = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState(null);
  const [companyHeader, setCompanyHeader] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) {
      setError('No commission request ID provided');
      setLoading(false);
      return;
    }
    fetchCommissionRequest();
  }, [id]);

  const fetchCommissionRequest = async () => {
    try {
      setLoading(true);
      
      const response = await api.get(`/commissionrequest/${id}`);
      console.log('Commission Request Data:', response.data);
      
      if (response.data) {
        setRequest(response.data);
        
        if (response.data.companyID) {
          await fetchCompanyHeader(response.data.companyID);
        }
      } else {
        setError('Commission request not found');
      }
    } catch (err) {
      console.error('Error fetching commission request:', err);
      setError(err.response?.data?.message || 'Failed to load commission request');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyHeader = async (companyId) => {
    try {
      const companyResponse = await api.get(`/companies/${companyId}`);
      console.log('Company Info:', companyResponse.data);
      
      if (companyResponse.data) {
        setCompanyInfo(companyResponse.data);
        
        if (companyResponse.data.header) {
          const imageUrl = convertImageToBase64(companyResponse.data.header);
          setCompanyHeader(imageUrl);
        }
      }
    } catch (err) {
      console.error('Error fetching company header:', err);
    }
  };

  const convertImageToBase64 = (imageData) => {
    if (typeof imageData === 'string' && imageData.startsWith('http')) {
      return imageData;
    }
    
    if (typeof imageData === 'string' && imageData.startsWith('data:image')) {
      return imageData;
    }
    
    try {
      if (Array.isArray(imageData)) {
        const byteArray = new Uint8Array(imageData);
        let binary = '';
        for (let i = 0; i < byteArray.length; i++) {
          binary += String.fromCharCode(byteArray[i]);
        }
        const base64 = btoa(binary);
        const imageType = detectImageType(imageData);
        return `data:image/${imageType};base64,${base64}`;
      }
      
      if (typeof imageData === 'string') {
        if (isBase64(imageData)) {
          return `data:image/png;base64,${imageData}`;
        }
      }
    } catch (error) {
      console.error('Error converting image to base64:', error);
    }
    
    return null;
  };

  const detectImageType = (imageData) => {
    try {
      if (Array.isArray(imageData) && imageData.length > 0) {
        const firstBytes = imageData.slice(0, 4);
        
        if (firstBytes[0] === 0xFF && firstBytes[1] === 0xD8) {
          return 'jpeg';
        }
        if (firstBytes[0] === 0x89 && firstBytes[1] === 0x50 && 
            firstBytes[2] === 0x4E && firstBytes[3] === 0x47) {
          return 'png';
        }
        if (firstBytes[0] === 0x47 && firstBytes[1] === 0x49 && 
            firstBytes[2] === 0x46 && firstBytes[3] === 0x38) {
          return 'gif';
        }
        if (firstBytes[0] === 0x57 && firstBytes[1] === 0x45 && 
            firstBytes[2] === 0x42 && firstBytes[3] === 0x50) {
          return 'webp';
        }
        if (firstBytes[0] === 0x42 && firstBytes[1] === 0x4D) {
          return 'bmp';
        }
      }
    } catch (error) {
      console.error('Error detecting image type:', error);
    }
    return 'png';
  };

  const isBase64 = (str) => {
    try {
      return btoa(atob(str)) === str;
    } catch (err) {
      return false;
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleBack = () => {
    navigate('/commissions/list');
  };

  const calculateTotalDeductions = () => {
    if (!request?.deductions) return 0;
    return request.deductions.reduce((sum, d) => sum + (d.debitAmount || 0), 0);
  };

  const calculateNetAmount = () => {
    if (!request) return 0;
    const totalDeductions = calculateTotalDeductions();
    return (request.creditAmount || 0) - totalDeductions;
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error" icon={<ErrorIcon />}>
          {error}
        </Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBack />}
          onClick={handleBack}
          sx={{ mt: 2 }}
        >
          Back to List
        </Button>
      </Container>
    );
  }

  if (!request) {
    return (
      <Container sx={{ py: 1 }}>
        <Alert severity="warning">No commission request data available</Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBack />}
          onClick={handleBack}
          sx={{ mt: 2 }}
        >
          Back to List
        </Button>
      </Container>
    );
  }

  const compactTableCell = {
    padding: '3px 6px',
    fontSize: '0.7rem',
    lineHeight: '1.2'
  };

  return (
    <Container maxWidth="lg" sx={{ py: 1 }} className="print-container">
      <Box sx={{ display: { xs: 'block', print: 'none' }, mb: 1 }} className="no-print">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={handleBack}
          >
            Back to List
          </Button>
          <Box>
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              sx={{ mr: 1 }}
            >
              Print
            </Button>
            <Button
              variant="outlined"
              startIcon={<PictureAsPdf />}
              onClick={handlePrint}
            >
              PDF
            </Button>
          </Box>
        </Box>
      </Box>

      <Paper 
        sx={{ 
          p: 2, 
          print: { 
            p: 1,
            boxShadow: 'none',
            border: 'none',
            margin: 0,
            borderRadius: 0
          }
        }}
        id="print-content"
        className="print-paper"
      >
        {/* Company Header Image - Stretch Mode */}
        {companyHeader ? (
          <Box sx={{ 
            width: '100%', 
            mb: 0.75,
            overflow: 'hidden',
            borderRadius: '2px',
            border: '1px solid #e0e0e0',
            backgroundColor: '#f5f5f5',
            height: '100px'
          }}>
            <img 
              src={companyHeader} 
              alt="Company Header" 
              style={{ 
                width: '100%',
                height: '100%',
                objectFit: 'fill',
                display: 'block'
              }}
              className="company-header-image"
              onError={(e) => {
                console.error('Error loading company header image');
                e.target.style.display = 'none';
                const fallback = document.getElementById('header-fallback');
                if (fallback) fallback.style.display = 'flex';
              }}
            />
          </Box>
        ) : (
          <Box 
            id="header-fallback"
            sx={{ 
              width: '100%', 
              mb: 0.75,
              p: 1.5,
              bgcolor: '#1976d2',
              borderRadius: '2px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100px'
            }}
          >
            <BusinessIcon sx={{ fontSize: 36, color: 'white', mr: 2 }} />
            <Typography variant="h5" color="white" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
              {companyInfo?.companyName || 'Company Name'}
            </Typography>
          </Box>
        )}

        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 0.5
        }}>
          <Box>
            <Typography variant="h5" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
              Commission Request
            </Typography>
            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.65rem' }}>
              Receipt: {request.receiptNo || 'N/A'}
            </Typography>
          </Box>
          <Box>
            <Chip
              label={request.isPaid ? 'PAID' : 'UNPAID'}
              color={request.isPaid ? 'success' : 'warning'}
              size="small"
              sx={{ height: '22px', '& .MuiChip-label': { fontSize: '0.7rem', px: 1 } }}
            />
          </Box>
        </Box>

        <Divider sx={{ mb: 0.75 }} />

        <Grid container spacing={1} sx={{ mb: 0.75 }}>
          <Grid item xs={6} md={6}>
            <Card variant="outlined" sx={{ '& .MuiCardContent-root': { p: 1 } }}>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontSize: '0.65rem' }}>
                  Agent Information
                </Typography>
                <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>
                  {request.agentName || 'N/A'}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.65rem' }}>
                  Agent ID: {request.agentID || 'N/A'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={6}>
            <Card variant="outlined" sx={{ '& .MuiCardContent-root': { p: 1 } }}>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontSize: '0.65rem' }}>
                  Transaction Details
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '0.8rem' }}>
                  Date: <strong>{request.transDate ? new Date(request.transDate).toLocaleDateString() : 'N/A'}</strong>
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.65rem' }}>
                  Created: {request.createdDate ? new Date(request.createdDate).toLocaleString() : 'N/A'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Employees Table - Without Footer */}
        <Typography variant="h6" gutterBottom sx={{ mt: 1, fontSize: '0.85rem' }}>
          Employees
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 0.5 }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
              <TableRow>
                <TableCell sx={{ ...compactTableCell, width: '5%', fontWeight: 600 }}>#</TableCell>
                <TableCell sx={{ ...compactTableCell, width: '40%', fontWeight: 600 }}>Full Name</TableCell>
                <TableCell sx={{ ...compactTableCell, width: '30%', fontWeight: 600 }}>Passport No</TableCell>
                <TableCell sx={{ ...compactTableCell, width: '25%', fontWeight: 600, textAlign: 'right' }}>S.No</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {request.applicants && request.applicants.length > 0 ? (
                request.applicants.map((applicant, index) => (
                  <TableRow key={applicant.id || applicant.ID || index}>
                    <TableCell sx={compactTableCell}>{index + 1}</TableCell>
                    <TableCell sx={compactTableCell}>
                      <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
                        {applicant.fullName || applicant.FullName || applicant.name || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={compactTableCell}>
                      <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
                        {applicant.passportNo || applicant.passport || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...compactTableCell, textAlign: 'right' }}>
                      <Chip 
                        label={applicant.sno || applicant.SNo || index + 1} 
                        size="small" 
                        sx={{ height: '18px', '& .MuiChip-label': { fontSize: '0.6rem', px: 0.5 } }}
                      />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={compactTableCell}>
                    <Typography color="textSecondary" sx={{ fontSize: '0.7rem' }}>No applicants</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Total Employees - Outside the table, only once */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          mb: 1.5,
          px: 1,
          py: 0.5,
          bgcolor: '#fafafa',
          borderRadius: '4px',
          border: '1px solid #e0e0e0'
        }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
            Total Employees: {request.applicants?.length || 0}
          </Typography>
        </Box>

        {/* Deductions Table - Without Footer */}
        <Typography variant="h6" gutterBottom sx={{ mt: 1, fontSize: '0.85rem' }}>
          Deductions
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 0.5 }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
              <TableRow>
                <TableCell sx={{ ...compactTableCell, width: '5%', fontWeight: 600 }}>#</TableCell>
                <TableCell sx={{ ...compactTableCell, width: '35%', fontWeight: 600 }}>Applicant</TableCell>
                <TableCell sx={{ ...compactTableCell, width: '35%', fontWeight: 600 }}>Reason</TableCell>
                <TableCell sx={{ ...compactTableCell, width: '25%', fontWeight: 600, textAlign: 'right' }}>Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {request.deductions && request.deductions.length > 0 ? (
                request.deductions.map((deduction, index) => (
                  <TableRow key={deduction.cid || deduction.CID || index}>
                    <TableCell sx={compactTableCell}>{index + 1}</TableCell>
                    <TableCell sx={compactTableCell}>
                      <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
                        {deduction.fullName || deduction.FullName || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={compactTableCell}>
                      <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
                        {deduction.reason || 'No reason provided'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...compactTableCell, textAlign: 'right', color: 'error.main' }}>
                      <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.7rem' }}>
                        ${deduction.debitAmount?.toFixed(2) || '0.00'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={compactTableCell}>
                    <Typography color="textSecondary" sx={{ fontSize: '0.7rem' }}>No deductions</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Total Deductions - Outside the table, only once */}
        {request.deductions && request.deductions.length > 0 && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            mb: 1.5,
            px: 1,
            py: 0.5,
            bgcolor: '#fafafa',
            borderRadius: '4px',
            border: '1px solid #e0e0e0'
          }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: '0.75rem', color: 'error.main' }}>
              Total Deductions: ${calculateTotalDeductions().toFixed(2)}
            </Typography>
          </Box>
        )}

        {/* Summary */}
        <Card variant="outlined" sx={{ mt: 1, bgcolor: '#fafafa', '& .MuiCardContent-root': { p: 1 } }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontSize: '0.85rem' }}>
              Financial Summary
            </Typography>
            <Grid container spacing={0.5}>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.65rem' }}>
                  Credit Amount
                </Typography>
                <Typography variant="h6" color="primary" sx={{ fontSize: '0.85rem' }}>
                  ${request.creditAmount?.toFixed(2) || '0.00'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.65rem' }}>
                  Total Deductions
                </Typography>
                <Typography variant="h6" color="error" sx={{ fontSize: '0.85rem' }}>
                  -${calculateTotalDeductions().toFixed(2)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.65rem' }}>
                  Net Amount
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="success.main" sx={{ fontSize: '1rem' }}>
                  ${calculateNetAmount().toFixed(2)}
                </Typography>
              </Grid>
            </Grid>
            {request.remark && (
              <Box sx={{ mt: 0.5 }}>
                <Divider sx={{ mb: 0.5 }} />
                <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.65rem' }}>
                  Remarks:
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '0.75rem' }}>
                  {request.remark}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Footer with generation time */}
        <Box sx={{ 
          mt: 1.5, 
          pt: 0.5, 
          borderTop: '1px solid #ddd', 
          textAlign: 'center',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 0.5
        }}>
          <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.6rem' }}>
            Generated on {new Date().toLocaleString()}
          </Typography>
        </Box>
      </Paper>

      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            
            .print-paper, 
            .print-paper * {
              visibility: visible;
            }
            
            .print-paper {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              margin: 0 !important;
              padding: 6px !important;
              box-shadow: none !important;
              border: none !important;
              background: white !important;
            }
            
            .print-container {
              padding: 0 !important;
              margin: 0 !important;
              max-width: 100% !important;
            }
            
            .no-print {
              display: none !important;
            }
            
            header.MuiAppBar-root {
              display: none !important;
            }
            
            nav, aside, footer, .MuiDrawer-root {
              display: none !important;
            }
            
            .MuiChip-root {
              print-color-adjust: exact;
              -webkit-print-color-adjust: exact;
            }
            
            .company-header-image {
              print-color-adjust: exact;
              -webkit-print-color-adjust: exact;
              width: 100% !important;
              height: 100% !important;
              object-fit: fill !important;
            }
            
            table {
              page-break-inside: auto;
            }
            
            tr {
              page-break-inside: avoid;
              page-break-after: auto;
            }
            
            thead {
              display: table-header-group;
            }
            
            /* Hide table footer in print */
            tfoot {
              display: none !important;
            }
            
            @page {
              margin: 0.5cm 0.5cm 1.2cm 0.5cm;
              size: A4 portrait;
              
              @bottom-center {
                content: "Page " counter(page) " of " counter(pages);
                font-size: 9px;
                font-family: Arial, Helvetica, sans-serif;
                color: #666;
                padding-top: 8px;
                width: 100%;
              }
            }
          }
          
          .company-header-image {
            width: 100%;
            height: 100%;
            object-fit: fill;
          }
        `}
      </style>
    </Container>
  );
};

export default CommissionRequestPrint;